/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { DigitizableLayer, DigitizePointConfigModel } from './digitize-point-config.model';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import { LoadDigitizeConfig } from './digitize-point-config.actions';
import { concatMapTo, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { LoggerService } from '../../../../shared/services/logger.service';
import { RestService } from '../../../../shared/services/rest.service';
import { MapConfigService } from '../../../services/mapConfig.service';
import { AuthState } from '../../../../../core/security/state/authState.state';
import { AppConfigState } from '../../../../../core/state/appConfig/app-config.state';
import { MapConfigState } from '../../../state/map-config.state';
import { Injectable } from '@angular/core';

@State<DigitizePointConfigModel>({
    name: 'digitize',
    defaults: {
        loaded: false,
        layers: [],
    },
})
@Injectable()
export class DigitizePointConfigState implements NgxsOnInit {
    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private mapConfig: MapConfigService,
        private store: Store
    ) {}

    ngxsOnInit(ctx: StateContext<any>) {
        this.store
            .select(AuthState.validated)
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                concatMapTo(this.store.select(MapConfigState.loaded)),
                distinctUntilChanged(),
                filter((b) => b),
                concatMapTo(this.store.selectOnce(AppConfigState.currentMap)),
                tap((mapName) => setTimeout(() => ctx.dispatch(new LoadDigitizeConfig(mapName)), 0)),
            ).subscribe();
    }

    @Action(LoadDigitizeConfig)
    loadConfig({ setState }, action: LoadDigitizeConfig) {
        return this.rest.apiGet<DigitizableLayer[]>(`map/digitize/layers/${action.mapName}`).pipe(
            map((layers) => {
                layers.forEach((layer: DigitizableLayer) => {
                    const mapLayer = this.mapConfig.getLayer(layer.layer);
                    layer.displayName = mapLayer.label;
                });
                return layers;
            }),
            tap((layers) => setState({ loaded: true, layers }))
        );
    }

    @Selector() static loaded(state: DigitizePointConfigModel) {
        return state.loaded;
    }
    @Selector() static config(state: DigitizePointConfigModel) {
        return state;
    }
    @Selector() static layers(state: DigitizePointConfigModel) {
        return state.layers;
    }
    @Selector() static canCreate(state: DigitizePointConfigModel) {
        return state.layers.some((layer) => layer.create);
    }
}
